/**
 * The A/B-experiment unit id and the QA forced arm, sent with the handoff as the top-level
 * `experiment` member (`docs/EXPERIMENTS.md` §4, D239).
 *
 * Runs on the merchant's own domain, inside the customer's browser. Nothing here may import a
 * value from `@/` — the bundle is self-contained (`docs/STOREFRONT.md`).
 *
 * ## `_peak_vid`, the visitor id
 *
 * The server assigns an experiment arm from a unit key: `v:{visitorId}` when the storefront
 * sends one, else `c:{cart.token}`. The visitor id is 128 random bits from
 * `crypto.getRandomValues` (never `Math.random`; no id at all when the API is missing),
 * base64url without padding — exactly 22 characters of `[A-Za-z0-9_-]`, {@link PEAK_VID_PATTERN}.
 * It lives in one first-party cookie on the shop origin, written like `_peak_gcl`
 * (`./url-click-ids.ts`): host-only (no `domain=`), `path=/`, `SameSite=Lax`, `Secure` on an
 * https page, `max-age` of 180 days. No `localStorage`. A cookie value that is not in the
 * pattern reads as absent and is replaced.
 *
 * ## Consent — Shopify's Customer Privacy API, analytics purpose
 *
 * A unit id is "customer analytics about how the storefront was used and interactions made on
 * the storefront" (https://shopify.dev/docs/api/customer-privacy, read 2026-10-14, the
 * description of `window.Shopify.customerPrivacy.analyticsProcessingAllowed()`), so that
 * method gates it; the `visitorConsentCollected` event's detail carries the same purpose as
 * `analyticsAllowed` (same page: "Updates are published only when consent changes"). The
 * rule mirrors `_peak_gcl`'s, purpose aside:
 *
 * 1. `analyticsProcessingAllowed` is a function → `true` keeps (or creates) the cookie;
 *    anything else (`false`, a non-boolean, a throw) deletes it. Either way a
 *    `visitorConsentCollected` listener follows the visitor's later answer: `analyticsAllowed`
 *    `true` creates, `false` deletes.
 * 2. Otherwise `Shopify.loadFeatures` is a function → load `consent-tracking-api` 0.1 exactly as
 *    `./url-click-ids.ts` does and apply rule 1 in the callback; a load error, a throw, or no
 *    method after a clean callback is the documented no-API case → create.
 * 3. Otherwise (no API, no loader: headless, the mock storefront) → create.
 *
 * ## Only on a store that runs an experiment (D244)
 *
 * The storefront config says `experiments: true` only when the store has experiments switched
 * on and at least one `RUNNING` experiment. Only then does the script call {@link initVisitorId}
 * (once, when the config answers) and send a unit at handoff; otherwise `_peak_vid` is neither
 * written nor read, so a store that runs no test sets no cookie. The QA forced arm below is
 * captured and sent regardless — a draft can be tested before anything runs.
 *
 * At handoff ({@link readExperimentHint}) consent is asked again, synchronously: the API says
 * no → the cookie is deleted and no unit is sent (withdrawn consent). With no API but a loader
 * on the page — the load may still be running — only a cookie the page-load pass already wrote
 * is sent, nothing new is created.
 *
 * ## `peak_arm`, the QA forced arm
 *
 * `?peak_arm=<QA token>` on any storefront URL (1–128 of `[A-Za-z0-9_-]`, {@link PEAK_ARM_PATTERN})
 * is kept in `sessionStorage` under `peak_arm` and sent as `forceArm` on every handoff of the
 * tab's session; `?peak_arm=` (empty) clears it; any other value is ignored. The value is a
 * signed, expiring token the admin mints (`src/lib/experiments/qa-token.ts`), never a raw arm id:
 * the script only forwards it, the server verifies it. Once captured, the parameter is taken out
 * of the address bar ({@link stripForcedArm}, `history.replaceState`, every other parameter and
 * the hash kept), so a pixel that logs the page URL on a later page view does not carry it. Not
 * consent-gated: it is a merchant's own QA link, not visitor data, and the server excludes
 * forced checkouts from every result.
 *
 * ## `consent: false` — declined means not enrolled
 *
 * When the Customer Privacy API answers analytics `false` at handoff (explicitly declined; a
 * throw counts as declined), the member carries `consent: false` and the server enrols nobody on
 * that handoff — not even by cart token — unless a valid QA token is present. The API absent or
 * not loaded yet sends nothing about consent: the server falls back to the cart token as before.
 * Asked whether or not the store runs an experiment (the config's `experiments` may lag the
 * server by a minute); only the cookie is left alone while it does not.
 *
 * Nothing here can throw into the caller, and nothing the handoff needs waits on it.
 */

import type { YcCaptureDocumentLike } from './url-click-ids';
import type { YcStorageLike } from './config';

/** The cookie this module writes. Host-only, `path=/`, 180 days. */
export const PEAK_VID_COOKIE = '_peak_vid';

/** 180 days (180 × 24 × 60 × 60). */
export const PEAK_VID_MAX_AGE_SECONDS = 15552000;

/** 16 random bytes, base64url, no padding. The server enforces the same shape. */
export const PEAK_VID_PATTERN = /^[A-Za-z0-9_-]{22}$/;

/** The URL parameter and the `sessionStorage` key of the QA forced arm. */
export const PEAK_ARM_KEY = 'peak_arm';

/** A QA token the storefront forwards. The server enforces the same shape, then verifies it. */
export const PEAK_ARM_PATTERN = /^[A-Za-z0-9_-]{1,128}$/;

/** The top-level `experiment` member of the handoff body; never sent empty. */
export type PeakExperimentHint = { unit?: string; forceArm?: string; consent?: false };

/** What the unit-id functions need from the page. */
export type PeakExperimentEnv = {
  document: YcCaptureDocumentLike | null | undefined;
  /** `window.Shopify`, whatever it is; only `customerPrivacy` and `loadFeatures` are read. */
  shopifyGlobal: unknown;
  /** `location.protocol === 'https:'` — adds `Secure` to the cookie. */
  secure: boolean;
  /**
   * Fills a byte array from a CSPRNG (`crypto.getRandomValues`). A throw (no `crypto` on the
   * page) means no new id is made; `Math.random` is never a substitute.
   */
  fill: (bytes: Uint8Array) => unknown;
};

/** Loose view of `window.Shopify`: the two members this module ever touches on it. */
type ShopifyLike =
  | { customerPrivacy?: { analyticsProcessingAllowed?: unknown } | null; loadFeatures?: unknown }
  | null
  | undefined;

/** The first `_peak_vid` in a cookie jar whose value has the id's shape. */
const VID_IN_JAR = /(?:^|;)\s*_peak_vid=([A-Za-z0-9_-]{22})\s*(?:;|$)/;

/** The `document.cookie` assignment that writes `value`, or deletes the cookie when `''`. */
export function peakVidSetCookie(value: string, secure: boolean): string {
  // The attributes are literals so the shipped bundle can be grepped for them
  // (`tests/unit/storefront/bundle.test.ts`); the unit test pins them to the constants above.
  return (
    PEAK_VID_COOKIE +
    '=' +
    value +
    '; path=/; max-age=' +
    (value ? '15552000' : '0') +
    '; SameSite=Lax' +
    (secure ? '; Secure' : '')
  );
}

/** A fresh visitor id: 16 CSPRNG bytes, base64url, no padding; `undefined` without a CSPRNG. */
export function newVisitorId(fill: PeakExperimentEnv['fill']): string | undefined {
  try {
    const bytes = new Uint8Array(16);
    fill(bytes);
    let binary = '';
    for (let i = 0; i < 16; i++) binary += String.fromCharCode(bytes[i] as number);
    // 16 bytes are 24 base64 characters, the last two `==` padding: 22 remain.
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').slice(0, 22);
  } catch {
    // No CSPRNG, no id: the server falls back to the cart token.
    return undefined;
  }
}

/** The `_peak_vid` value in a document's cookie jar, when it has the id's shape. */
export function readPeakVid(
  documentLike: YcCaptureDocumentLike | null | undefined,
): string | undefined {
  try {
    const jar = documentLike && documentLike.cookie;
    const match = typeof jar === 'string' ? VID_IN_JAR.exec(jar) : null;
    if (match) return match[1];
  } catch {
    // A cookie jar that throws is "no cookie".
  }
  return undefined;
}

/** The stored id, creating it when absent; `undefined` when the browser does not keep it. */
function ensure(env: PeakExperimentEnv): string | undefined {
  const documentLike = env.document;
  let id = readPeakVid(documentLike);
  if (!id && documentLike && (id = newVisitorId(env.fill))) {
    try {
      documentLike.cookie = peakVidSetCookie(id, env.secure);
    } catch {
      // A jar that refuses the write is "no cookie"; the re-read below says so.
    }
    // Only an id the browser actually kept is a stable unit.
    id = readPeakVid(documentLike);
  }
  return id;
}

/** Delete the cookie when it is there. */
function drop(env: PeakExperimentEnv): void {
  try {
    if (env.document && readPeakVid(env.document)) {
      env.document.cookie = peakVidSetCookie('', env.secure);
    }
  } catch {
    // Nothing to undo.
  }
}

/** `true`/`false` from the Customer Privacy API, `undefined` when the method is not there. */
function analyticsAllowed(shopifyGlobal: unknown): boolean | undefined {
  const api = shopifyGlobal ? (shopifyGlobal as NonNullable<ShopifyLike>).customerPrivacy : null;
  const method = api ? api.analyticsProcessingAllowed : undefined;
  if (typeof method !== 'function') return undefined;
  try {
    return method.call(api) === true;
  } catch {
    // A consent tool that throws is no licence to write.
    return false;
  }
}

/** `window.Shopify.loadFeatures` when it is a function. */
function loaderOf(shopifyGlobal: unknown): unknown {
  const loader = shopifyGlobal ? (shopifyGlobal as NonNullable<ShopifyLike>).loadFeatures : null;
  return typeof loader === 'function' ? loader : undefined;
}

/**
 * The page-load pass: create or delete the cookie as consent says, and follow later consent
 * changes (module comment, rules 1–3). Never throws.
 */
export function initVisitorId(env: PeakExperimentEnv): void {
  try {
    const shopify = env.shopifyGlobal;
    const decide = (allowed: boolean): void => {
      if (allowed) ensure(env);
      else drop(env);
      const documentLike = env.document;
      if (documentLike && documentLike.addEventListener) {
        documentLike.addEventListener('visitorConsentCollected', (event) => {
          const detail = (event as { detail?: { analyticsAllowed?: unknown } | null }).detail;
          const answer = detail ? detail.analyticsAllowed : undefined;
          if (answer === true) ensure(env);
          else if (answer === false) drop(env);
        });
      }
    };

    const now = analyticsAllowed(shopify);
    if (now !== undefined) return decide(now);

    const loader = loaderOf(shopify);
    if (loader) {
      try {
        (loader as (features: unknown, done: (error: unknown) => void) => void).call(
          shopify,
          [{ name: 'consent-tracking-api', version: '0.1' }],
          (error) => {
            const loaded = error ? undefined : analyticsAllowed(shopify);
            if (loaded === undefined) ensure(env);
            else decide(loaded);
          },
        );
        return;
      } catch {
        // A loader that throws cannot give us the API: the no-API case.
      }
    }
    ensure(env);
  } catch {
    // Experiments never stand between the customer and the checkout.
  }
}

/**
 * Keep the QA forced arm from a query string: a valid `peak_arm` is stored, an empty one
 * clears the stored arm, anything else is ignored. Never throws.
 */
export function captureForcedArm(search: string, storage: YcStorageLike): void {
  try {
    const arm = new URLSearchParams(search).get(PEAK_ARM_KEY);
    if (arm === '') storage.removeItem(PEAK_ARM_KEY);
    else if (arm !== null && PEAK_ARM_PATTERN.test(arm)) storage.setItem(PEAK_ARM_KEY, arm);
  } catch {
    // A storage that refuses is "no forced arm".
  }
}

/** What {@link stripForcedArm} needs from `window.location` and `window.history`. */
export type PeakUrlEnv = {
  location: { pathname: string; search: string; hash: string };
  history: { state: unknown; replaceState: (state: unknown, unused: string, url: string) => void };
};

/**
 * Take `peak_arm` out of the address bar once it is captured, with `history.replaceState`: the
 * path, every other parameter (byte for byte, in order) and the hash stay; history state is kept;
 * nothing happens when the bar has no `peak_arm`. Never throws.
 */
export function stripForcedArm(page: PeakUrlEnv): void {
  try {
    const location = page.location;
    const parts = location.search.slice(1).split('&');
    const kept = parts.filter((part) => part.split('=')[0] !== PEAK_ARM_KEY);
    if (kept.length < parts.length) {
      page.history.replaceState(
        page.history.state,
        '',
        location.pathname + (kept.length ? '?' + kept.join('&') : '') + location.hash,
      );
    }
  } catch {
    // An address bar we cannot rewrite keeps the parameter; the token still expires.
  }
}

/**
 * The `experiment` member for one handoff, or `undefined` when there is no unit, no forced arm
 * and no declined consent. Re-asks consent: a "no" now deletes the cookie, sends no unit and
 * sends `consent: false` (the server then enrols nobody but a QA checkout). Never throws.
 *
 * `running` is the config's `experiments` (D244): `false` — the store runs no experiment, or has
 * experiments switched off — leaves `_peak_vid` alone entirely (not read, not written, not
 * deleted); the forced arm and a declined consent are still sent.
 */
export function readExperimentHint(
  env: PeakExperimentEnv,
  storage: YcStorageLike,
  running: boolean,
): PeakExperimentHint | undefined {
  const hint: PeakExperimentHint = {};
  try {
    const allowed = analyticsAllowed(env.shopifyGlobal);
    if (allowed === false) hint.consent = false;
    let unit: string | undefined;
    if (!running) {
      // D244: `_peak_vid` is not touched at all.
    } else if (allowed === false) drop(env);
    // No API but a loader: the page-load pass decides; send only what it already wrote.
    else if (allowed === undefined && loaderOf(env.shopifyGlobal)) unit = readPeakVid(env.document);
    else unit = ensure(env);
    if (unit) hint.unit = unit;
  } catch {
    // No unit; the server falls back to the cart token.
  }
  try {
    const arm = storage.getItem(PEAK_ARM_KEY);
    if (arm !== null && PEAK_ARM_PATTERN.test(arm)) hint.forceArm = arm;
  } catch {
    // A storage that throws is "no forced arm".
  }
  return hint.unit || hint.forceArm || hint.consent === false ? hint : undefined;
}
